<template>
  <b-container>
    <p class="mt-3 text-center">Select a site and date to view sign-ins</p>
    <div v-if="eligibleSites.length > 0" class="sign-ins-filter mt-3">
      <div class="dropdown">
        <vue-typeahead-bootstrap
          v-model="siteSearch"
          :data="eligibleSites"
          :serializer="site => site.text"
          @hit="selectSite($event)"
          :minMatchingChars="minChars"
          :showOnFocus="true"
          placeholder="Select a site"
        />
      </div>
      <div class="position-relative">
        <b-form-datepicker id="start-date" v-model="date" :max="today"></b-form-datepicker>
      </div>
    </div>
    <b-table v-if="!loading && site && signIns.length > 0" class="mt-3" :items="signIns" :fields="tableFields" responsive="md">
      <template v-slot:cell(phoneNumber)="data">
        <a v-if="data.value" :href="`tel:${data.value}`">{{ data.value }}</a>
      </template>
    </b-table>
    <b-alert v-if="!loading && site && signIns.length === 0" show style="z-index: 1000">No sign-ins on this day</b-alert>
    <div v-if="loading" class="w-100 text-center">
      <font-awesome-icon icon="spinner" spin class="icon"/>
    </div>
    <div v-if="!loading && site && signIns.length > 0" class="mb-4">
        <b-button v-on:click="csvExport" class="d-flex align-items-center justify-content-between btn-export">
          Export CSV
          <b-icon class="ml-2" icon="file-earmark-arrow-down"></b-icon>
        </b-button>
    </div>
  </b-container>
</template>

<script>
import moment from 'moment-timezone';
import { getSites, getSite, getSignIns } from '@/lib/api';
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap';
import { BIcon, BIconFileEarmarkArrowDown } from 'bootstrap-vue';

export default {
  name: 'SignInList',
  components: {
    VueTypeaheadBootstrap,
    BIcon,
    BIconFileEarmarkArrowDown, // eslint-disable-line vue/no-unused-components
  },
  data() {
    return {
      site: null,
      siteSearch: '',
      minChars: 0,
      eligibleSites: [],
      loading: true,
      date: '',
      endDate: '',
      today: new Date(),
      signIns: [],
      tableFields: [
        { key: 'name', label: 'Name', sortable: true },
        {
          key: 'signInTime',
          label: 'In',
          sortable: true,
          formatter: 'timeFormat',
          tdClass: 'text-right',
          thClass: 'text-center',
        },
        {
          key: 'signOutTime',
          label: 'Out',
          sortable: true,
          formatter: 'signOutTimeFormat',
          tdClass: 'text-right',
          thClass: 'text-center',
        },
        { key: 'company', label: 'Company', sortable: true },
        {
          key: 'visiting',
          label: 'Visiting',
          sortable: true,
          formatter: 'visitingFormat',
        },
        { key: 'phoneNumber', label: 'ph', sortable: false },
        { key: 'emailAddress', label: 'email', sortable: true },
      ],
    };
  },
  async mounted() {
    this.siteId = this.$route.params.siteId;
    this.eligibleSites = (await getSites()).map((site) => ({ value: site.id, text: `${site.jobNumber} - ${site.name}` }));
    this.date = this.$route.query.date || moment().tz('Pacific/Auckland').format('YYYY-MM-DD');
    this.endDate = this.$route.query.date || moment().tz('Pacific/Auckland').format('YYYY-MM-DD');
    if (this.siteId) {
      this.site = await getSite(this.siteId);
    }
    await this.loadSignIns();
  },
  watch: {
    async $route(to, from) {
      if (to.params.siteId !== from.params.siteId) {
        // update sign ins
        this.site = await getSite(this.siteId);
        await this.loadSignIns();
      }
    },
    async date(to, from) {
      if (to !== from) {
        await this.loadSignIns();
      }
    },
    async endDate(to, from) {
      if (to !== from) {
        await this.loadSignIns();
      }
    },
  },
  methods: {
    async selectSite(selectedSite) {
      if (selectedSite) {
        this.siteId = selectedSite.value;
        this.siteName = selectedSite.text;
        this.loading = true;

        this.site = await getSite(this.siteId);
        await this.loadSignIns();
      }
    },
    async loadSignIns() {
      if (this.site) {
        this.loading = true;
        this.signIns = await getSignIns(this.siteId, this.date);
      }
      this.loading = false;
    },
    timeFormat(dateTime) {
      if (!dateTime) {
        return '';
      }
      return moment(dateTime).tz('Pacific/Auckland').format('h:mm A');
    },
    signOutTimeFormat(value, key, item) {
      // sign out time has been set
      if (value) { return this.timeFormat(value); }

      // we are still on sign in day.
      const today = moment(new Date()).tz('Pacific/Auckland');
      const signInTime = moment(item.signInTime).tz('Pacific/Auckland');
      if (signInTime.isSame(today, 'day')) {
        return '';
      }

      // signin was in the past, so display auto logged out at midnight of signin day.
      const signOutTime = moment(item.signInTime).tz('Pacific/Auckland').endOf('day');
      return this.timeFormat(signOutTime);
    },
    visitingFormat(visiting) {
      if (visiting.startsWith('N/A')) {
        return '';
      }

      return visiting;
    },
    csvExport() {
      const excludedColumns = ['responses', 'longid', 'id', 'siteId'];
      let csvContent = 'data:text/csv;charset=utf-8,';
      csvContent += [
        // column names
        ['Site'].concat(Object.keys(this.signIns[0]))
          .filter((k) => !excludedColumns.includes(k)).join(','),
        // sign-in rows
        ...this.signIns
          // remove excludedColumns
          .map((item) => Object.keys(item)
            .filter((key) => !excludedColumns.includes(key))
            .reduce((obj, key) => {
              obj[key] = item[key]; // eslint-disable-line no-param-reassign
              return obj;
            }, {}))
          // create CSV row
          .map((item) => `"${
            [this.site.name].concat(Object.values(item))
              .map((val) => (val || ' '))
              .map((val) => (val ? val.replace(/"/g, '\\"').replace(/#/g, '%23') : 'N/A'))
              .join('","')
          }"`),
      ]
        .join('\n');

      const data = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', data);
      link.setAttribute('download', `${this.site.name.replace(/[^a-zA-Z0-9]+/g, '-')}-${moment(this.date).tz('Pacific/Auckland').format('YYYY-MM-DD')}-export.csv`);
      link.click();
    },
  },
};
</script>
